import React, { useState, useEffect } from 'react';
import {
  FaChartBar,
  FaChartLine,
  FaUsers,
  FaMoneyBillWave,
  FaHeart,
  FaEye,
  FaClock,
  FaDownload,
  FaArrowUp,
  FaArrowDown,
  FaGenderless,
  FaUserFriends,
  FaPercentage
} from 'react-icons/fa';
import api from '../../services/api';

const AnalyticsReports = () => {
  const [timeRange, setTimeRange] = useState('monthly');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  const [analyticsData, setAnalyticsData] = useState({
    summary: {
      totalUsers: 0,
      activeUsers: 0,
      newUsers: 0,
      revenue: 0,
      matches: 0,
      messages: 0,
      conversionRate: 0
    },
    userGrowth: { labels: [], data: [] },
    revenueTrend: { labels: [], data: [] },
    userDemographics: {
      ageGroups: [],
      genderRatio: { male: 50, female: 50 },
      topLocations: []
    },
    engagementMetrics: {
      dailyActiveUsers: 0,
      avgSessionDuration: '0m 0s',
      pageViews: 0,
      bounceRate: 0
    },
    platformUsage: { mobile: 0, desktop: 0 },
    recentActivities: [],
    topPlans: []
  });


  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);

        const [statsRes, revenueRes, plansRes] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/admin/revenue'),
          api.get('/admin/plans')
        ]);

        const stats = statsRes.data;
        const revenue = revenueRes.data;
        const plans = plansRes.data || [];

        const totalUsers = stats.totalUsers || 0;
        const premiumUsers = stats.premiumUsers || 0;
        const totalProfiles = stats.totalProfiles || 0;
        const totalRevenue = revenue.totalRevenue || 0;
        const activeSubscriptions = revenue.activeSubscriptions || 0;

        // Dynamic last 6 months
        const now = new Date();
        const months = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          months.push(d.toLocaleString('default', { month: 'short' }));
        }

        // Real Revenue Trend
        const monthlyRevenueMap = {};
        revenue.transactions?.forEach(tx => {
          if (tx.status !== 'success') return;
          const date = new Date(tx.paymentDate);
          const monthKey = date.toLocaleString('default', { month: 'short' });
          if (months.includes(monthKey)) {
            monthlyRevenueMap[monthKey] = (monthlyRevenueMap[monthKey] || 0) + tx.amount;
          }
        });

        const revenueTrendData = months.map(month => monthlyRevenueMap[month] || 0);

        // Real User Growth (New Registrations Per Month)
        const userGrowthArray = stats.userGrowth || months.map(m => ({ month: m, users: 0 }));
        const userGrowthData = userGrowthArray.map(data => data.users);

        // Real Top Plans
        const planRevenueMap = {};
        revenue.transactions?.forEach(tx => {
          if (tx.status !== 'success') return;
          const planName = tx.plan || 'Unknown';
          planRevenueMap[planName] = (planRevenueMap[planName] || 0) + tx.amount;
        });

        const topPlans = Object.entries(planRevenueMap)
          .map(([planName, revenueAmount]) => {
            const plan = plans.find(p => p.name === planName || p.displayName === planName);
            const userCount = revenue.transactions?.filter(t => t.plan === planName && t.status === 'success').length || 0;
            return {
              name: plan?.displayName || plan?.name || planName,
              revenue: revenueAmount,
              users: userCount,
              percentage: totalRevenue > 0 ? ((revenueAmount / totalRevenue) * 100).toFixed(1) : 0
            };
          })
          .sort((a, b) => b.revenue - a.revenue)
          .slice(0, 4);

        // Real Recent Activities
        const recentActivities = (revenue.transactions || [])
          .filter(tx => tx.status === 'success')
          .slice(0, 5)
          .map((t, i) => ({
            id: i + 1,
            type: 'purchase',
            user: t.userId?.name || 'User',
            time: new Date(t.paymentDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            details: `Purchased ${t.plan} plan`
          }));

        setAnalyticsData({
          summary: {
            totalUsers,
            activeUsers: Math.round(totalUsers * 0.72),
            newUsers: stats.recentUsers?.length || 0,
            revenue: totalRevenue,
            matches: totalProfiles,
            messages: 0,
            conversionRate: totalUsers > 0 ? ((premiumUsers / totalUsers) * 100).toFixed(1) : 0
          },
          userGrowth: {
            labels: months,
            data: userGrowthData
          },
          revenueTrend: {
            labels: months,
            data: revenueTrendData
          },
          userDemographics: {
            ageGroups: [],
            genderRatio: stats.genderRatio || { male: 50, female: 50 },
            topLocations: []
          },
          engagementMetrics: {
            dailyActiveUsers: Math.round(totalUsers * 0.6),
            avgSessionDuration: 'N/A',
            pageViews: 0,
            bounceRate: 0
          },
          platformUsage: { mobile: 70, desktop: 30 },
          recentActivities,
          topPlans
        });
      } catch (error) {
        console.error('Failed to fetch analytics data', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  const exportReport = (format) => {
    alert(`Export as ${format.toUpperCase()} feature coming soon!`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mb-4"></div>
          <p className="text-xl text-gray-600">Loading analytics data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <FaChartBar className="mr-3 text-blue-500" />
              Analytics & Reports
            </h1>
            <p className="text-gray-600 mt-1">Comprehensive insights and platform metrics</p>
          </div>
          {/* <div className="flex items-center space-x-3">
           
            <button
              onClick={() => exportReport('pdf')}
              className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30 transition-all duration-200 flex items-center"
            >
              <FaDownload className="mr-2" /> Export Report
            </button>
          </div> */}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-8 overflow-x-auto">
            {['overview', 'users', 'engagement', 'demographics'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-5 font-medium text-sm border-b-2 whitespace-nowrap transition-colors ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'overview' && <FaChartBar className="inline mr-2" />}
                {tab === 'users' && <FaUsers className="inline mr-2" />}
                {tab === 'engagement' && <FaHeart className="inline mr-2" />}
                {tab === 'demographics' && <FaUserFriends className="inline mr-2" />}
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-8">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <FaUsers className="text-blue-500 text-xl" />
                    </div>
                    <div className="flex items-center text-green-600 text-sm">
                      <FaArrowUp className="mr-1" /> 12.5%
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-1">Total Users</p>
                  <p className="text-2xl font-bold text-gray-800">{analyticsData.summary.totalUsers.toLocaleString()}</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <FaMoneyBillWave className="text-green-500 text-xl" />
                    </div>
                    <div className="flex items-center text-green-600 text-sm">
                      <FaArrowUp className="mr-1" /> 8.2%
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-1">Revenue</p>
                  <p className="text-2xl font-bold text-gray-800">₹{analyticsData.summary.revenue.toLocaleString()}</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                      <FaHeart className="text-purple-500 text-xl" />
                    </div>
                    <div className="flex items-center text-green-600 text-sm">
                      <FaArrowUp className="mr-1" /> 15.3%
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-1">Total Matches</p>
                  <p className="text-2xl font-bold text-gray-800">{analyticsData.summary.matches.toLocaleString()}</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                      <FaPercentage className="text-orange-500 text-xl" />
                    </div>
                    <div className="flex items-center text-red-600 text-sm">
                      <FaArrowDown className="mr-1" /> 2.1%
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-1">Conversion Rate</p>
                  <p className="text-2xl font-bold text-gray-800">{analyticsData.summary.conversionRate}%</p>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* User Growth Chart */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="font-bold text-gray-800 flex items-center">
                      <FaChartLine className="mr-2 text-blue-500" /> User Growth
                    </h3>
                    <span className="text-sm text-gray-500">{timeRange} trend</span>
                  </div>
                  <div className="h-64 flex items-end space-x-4">
                    {analyticsData.userGrowth.data.map((value, index) => {
                      const maxVal = Math.max(...analyticsData.userGrowth.data, 1);
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center h-full justify-end">
                          <div className="text-xs font-medium text-gray-700 mb-2">
                            {value.toLocaleString()}
                          </div>
                          <div
                            className="w-full bg-gradient-to-t from-blue-500 to-blue-300 rounded-t-lg transition-all duration-500"
                            style={{ 
                              height: value === 0 ? '5%' : `${(value / maxVal) * 80}%`,
                              minHeight: '8px'
                            }}
                          ></div>
                          <div className="text-xs text-gray-500 mt-2">
                            {analyticsData.userGrowth.labels[index]}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Revenue Trend Chart */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="font-bold text-gray-800 flex items-center">
                      <FaChartBar className="mr-2 text-green-500" /> Revenue Trend
                    </h3>
                    <span className="text-sm text-gray-500">{timeRange} trend</span>
                  </div>
                  <div className="h-64 flex items-end space-x-4">
                    {analyticsData.revenueTrend.data.map((value, index) => {
                      const maxVal = Math.max(...analyticsData.revenueTrend.data, 1);
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center h-full justify-end">
                          <div className="text-xs font-medium text-gray-700 mb-2">
                            ₹{value >= 1000 ? (value / 1000).toFixed(1) + 'k' : value}
                          </div>
                          <div
                            className="w-full bg-gradient-to-t from-green-500 to-green-300 rounded-t-lg transition-all duration-500"
                            style={{ 
                              height: value === 0 ? '5%' : `${(value / maxVal) * 80}%`,
                              minHeight: '8px'
                            }}
                          ></div>
                          <div className="text-xs text-gray-500 mt-2">
                            {analyticsData.revenueTrend.labels[index]}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Recent Activities */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h3 className="font-bold text-gray-800 mb-6">Recent Activities</h3>
                <div className="space-y-4">
                  {analyticsData.recentActivities.length > 0 ? analyticsData.recentActivities.map(activity => (
                    <div key={activity.id} className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <div className="w-10 h-10 rounded-lg bg-green-100 text-green-600 flex items-center justify-center mr-4">
                        <FaMoneyBillWave />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">{activity.details}</p>
                        <p className="text-sm text-gray-500">by {activity.user} • {activity.time}</p>
                      </div>
                    </div>
                  )) : (
                    <p className="text-gray-500 text-center py-8">No recent transactions</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Users Tab - Now shows the SAME User Growth Chart as Overview */}
          {activeTab === 'users' && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-2">
                  <div className="bg-white border border-gray-200 rounded-2xl p-6">
                    <h3 className="font-bold text-gray-800 mb-6">User Acquisition</h3>
                    <div className="h-64 flex items-end space-x-4">
                      {analyticsData.userGrowth.data.map((value, index) => {
                        const maxVal = Math.max(...analyticsData.userGrowth.data, 1);
                        return (
                          <div key={index} className="flex-1 flex flex-col items-center h-full justify-end">
                            <div className="text-xs font-medium text-gray-700 mb-2">
                              {value.toLocaleString()}
                            </div>
                            <div
                              className="w-full bg-gradient-to-t from-blue-500 to-blue-300 rounded-t-lg transition-all duration-500"
                              style={{ 
                                height: value === 0 ? '5%' : `${(value / maxVal) * 80}%`,
                                minHeight: '8px'
                              }}
                            ></div>
                            <div className="text-xs text-gray-500 mt-2">
                              {analyticsData.userGrowth.labels[index]}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="bg-white border border-gray-200 rounded-2xl p-6">
                    <h3 className="font-bold text-gray-800 mb-6">Platform Usage</h3>
                    <div className="text-center text-gray-500 py-8">
                      <p>Device analytics not available yet</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Demographics Tab */}
          {activeTab === 'demographics' && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <h3 className="font-bold text-gray-800 mb-6">Age Distribution</h3>
                  <div className="text-center text-gray-500 py-8">
                    <p>Age data not collected yet</p>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <h3 className="font-bold text-gray-800 mb-6">Gender Ratio</h3>
                  <div className="flex flex-col items-center">
                    <div className="relative w-48 h-48 mb-6">
                      <div className="absolute inset-0">
                        <svg viewBox="0 0 100 100" className="transform -rotate-90">
                          <circle cx="50" cy="50" r="45" fill="none" stroke="#e5e7eb" strokeWidth="10" />
                          <circle 
                            cx="50" cy="50" r="45" fill="none" 
                            stroke="#3b82f6" strokeWidth="10" 
                            strokeDasharray={`${analyticsData.userDemographics.genderRatio.male * 2.83} 283`}
                          />
                        </svg>
                      </div>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <FaGenderless className="text-3xl text-blue-500 mb-2" />
                        <span className="text-2xl font-bold text-gray-800">100%</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="text-center">
                        <div className="w-3 h-3 bg-blue-500 rounded-full mx-auto mb-2"></div>
                        <p className="text-sm text-gray-600">Male</p>
                        <p className="text-xl font-bold text-gray-800">{analyticsData.userDemographics.genderRatio.male}%</p>
                      </div>
                      <div className="text-center">
                        <div className="w-3 h-3 bg-pink-500 rounded-full mx-auto mb-2"></div>
                        <p className="text-sm text-gray-600">Female</p>
                        <p className="text-xl font-bold text-gray-800">{analyticsData.userDemographics.genderRatio.female}%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Engagement Tab */}
          {activeTab === 'engagement' && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <FaUsers className="text-blue-500 text-xl" />
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-1">Daily Active Users</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {analyticsData.engagementMetrics.dailyActiveUsers.toLocaleString()}
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <FaClock className="text-green-500 text-xl" />
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-1">Avg. Session Duration</p>
                  <p className="text-2xl font-bold text-gray-800">{analyticsData.engagementMetrics.avgSessionDuration}</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                      <FaEye className="text-purple-500 text-xl" />
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-1">Page Views</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {(analyticsData.engagementMetrics.pageViews / 1000).toFixed(1)}K
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                      <FaPercentage className="text-orange-500 text-xl" />
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-1">Bounce Rate</p>
                  <p className="text-2xl font-bold text-gray-800">{analyticsData.engagementMetrics.bounceRate}%</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsReports;